import { PostPaymentRequest, postPostPaymentRequest, YouTubeInfluencerPost } from '@round/api';
import { Modal, ModalTitle, Button } from '@round/ui-kit';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import useNonNullContext from 'Hooks/useNonNullContext';
import { useMemo } from 'react';
import styles from './YoutubePostPaymentRequestModal.module.css';
import { asMoney, showNotification } from 'helpers';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import PostPaymentRequestForm, { PaymentRequestFormValues } from '../components/PostPaymentRequestForm';
import { baseInitialValues, getPaymentMethodAPIValues, validationSchema } from '../helpers';

type Props = {
    post: YouTubeInfluencerPost | null;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (paymentRequest: PostPaymentRequest) => void;
};

const YoutubePostPaymentRequestModal = ({ post, isOpen, onClose, onSuccess }: Props) => {
    const { currencies } = useNonNullContext(OptionsContext);
    const currencyOptions = currencies.map((c) => ({
        value: c.id,
        label: c.name,
    }));

    const initialValues: PaymentRequestFormValues = useMemo(
        () => ({
            ...baseInitialValues,
            amount: post?.cost || '',
            currency: currencyOptions.find((curr) => curr.value === post?.currency_id),
            paypal_email_address: '',
        }),
        [currencyOptions, post?.cost, post?.currency_id]
    );

    const handleSubmit = async (values: PaymentRequestFormValues, helpers: FormikHelpers<PaymentRequestFormValues>) => {
        if (typeof post?.id !== 'number' || !values.currency?.value) {
            return;
        }

        try {
            const response = await postPostPaymentRequest({
                amount: values.amount,
                currency: values.currency?.value,
                youtube_influencer_post: post?.id,
                ...getPaymentMethodAPIValues(values),
            });

            if (response.status === 201) {
                showNotification('Payment request created', 'info');
                onClose();
                onSuccess(response.data);
                return;
            }

            if (response.status === 400) {
                if (Array.isArray(response.data)) {
                    showNotification(response.data.join('. '), 'error');
                    return;
                }

                helpers.setErrors(mapApiErrorsToFormikErrors<PaymentRequestFormValues>(response.data));
                return;
            }

            showNotification('Could not request payment', 'error');
        } catch {
            showNotification('Could not request payment', 'error');
        }
    };

    const formikProps = useFormik<PaymentRequestFormValues>({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    });

    const postCurrency = currencies.find((curr) => curr.id === post?.currency_id);
    const postCostDisplayValue = post?.cost && postCurrency ? asMoney(parseFloat(post.cost), postCurrency) : '-';

    const handleClose = () => {
        formikProps.resetForm();
        onClose();
    };

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={handleClose} className={styles.container}>
            <ModalTitle>Request Payment</ModalTitle>
            <dl className={styles.detailsContainer}>
                <div>
                    <dt>Post cost</dt>
                    <dd>{postCostDisplayValue}</dd>
                </div>
            </dl>

            <FormikProvider value={formikProps}>
                <PostPaymentRequestForm />
            </FormikProvider>

            <Button
                type="filled"
                color="black"
                loading={formikProps.isSubmitting}
                disabled={!formikProps.isValid}
                className={styles.submitButton}
                onClick={formikProps.submitForm}
            >
                Submit
            </Button>
        </Modal>
    );
};

export default YoutubePostPaymentRequestModal;
